<template>
  <section class="mainSection">
    <template v-if="loaded">
      <section class="form-section" style="margin-bottom: 20px;">
        <h5>Pessoa</h5>
        <pessoa
          :loadPessoa="loadPessoa"
          :branch="branch"
          :action="action"
          @savePessoa="savePessoa"
          @closeModalPessoa="$emit('closeModalPessoa')"
        />
      </section>
      <section
        v-if="isResponsible"
        class="form-section access-section"
        aria-live="polite"
      >
        <div class="access-heading">
          <div>
            <h5>Acesso do responsável {{ responsibleLabel }}</h5>
            <p>Validação do usuário usado para entrar no Portal da Família.</p>
          </div>
          <button
            v-if="responsibleUserError"
            type="button"
            class="access-retry"
            :disabled="responsibleUserLoading"
            @click="loadResponsibleUserStatus"
          >
            Tentar novamente
          </button>
        </div>

        <div v-if="responsibleUserLoading" class="access-loading">
          <span class="access-loader" aria-hidden="true"></span>
          <span>Consultando usuário ativo...</span>
        </div>

        <div v-else-if="responsibleUserError" class="access-state access-state--error">
          <span class="access-indicator" aria-hidden="true"></span>
          <div>
            <strong>Não foi possível validar o acesso</strong>
            <p>{{ responsibleUserError }}</p>
          </div>
        </div>

        <div
          v-else-if="responsibleUserStatus"
          :class="['access-state', `access-state--${ responsibleStatusContent.tone }`]"
        >
          <span class="access-indicator" aria-hidden="true"></span>
          <div class="access-content">
            <div class="access-summary">
              <div>
                <strong>{{ responsibleStatusContent.title }}</strong>
                <p>{{ responsibleStatusContent.description }}</p>
              </div>
              <button
                v-if="responsibleUserStatus.acao"
                type="button"
                class="btn btn-primary btn-small"
                :disabled="responsibleUserActionLoading"
                @click="syncResponsibleUser"
              >
                {{ responsibleUserActionLoading ? 'Processando...' : responsibleStatusContent.actionLabel }}
              </button>
            </div>

            <dl
              v-if="responsibleUserStatus.emailPessoa || responsibleUserStatus.emailUsuario"
              class="access-emails"
            >
              <div v-if="responsibleUserStatus.emailPessoa">
                <dt>E-mail salvo na pessoa</dt>
                <dd>{{ responsibleUserStatus.emailPessoa }}</dd>
              </div>
              <div v-if="responsibleUserStatus.emailUsuario">
                <dt>E-mail atual de login</dt>
                <dd>{{ responsibleUserStatus.emailUsuario }}</dd>
              </div>
            </dl>

            <small>
              A comparação considera o e-mail já salvo. Se ele foi editado agora,
              salve os dados cadastrais antes de sincronizar.
            </small>
          </div>
        </div>
      </section>
      <section class="form-section">
        <h5>Endereço</h5>
        <Endereco
            :action="action"
            :branch="branch"
            :autoPopulate="loadPessoa?.endereco"
        />
      </section>
      <Footer
        @savePessoa="savePessoa"
        @closeModalPessoa="$emit('closeModalPessoa')"
      />
    </template>
  </section>
</template>

<script>
import Footer from '@/components/centralaluno/utilities/Footer.vue'
import Pessoa from '@/components/matriculas/4/form/Pessoas.vue'
import Endereco from '@/components/matriculas/4/form/Endereco.vue'
import MatriculaService from '@/services/matricula.service'
import { unformatDate } from '@/services/utils.service'

export default {
  components: {
    Footer, Pessoa, Endereco
  },
  props: {
    idPessoa: null,
    branch: null,
    action: null,
    personContext: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      loadPessoa: null,
      loaded: false,
      responsibleUserStatus: null,
      responsibleUserLoading: false,
      responsibleUserActionLoading: false,
      responsibleUserError: null
    }
  },
  computed: {
    responsibleType () {
      const types = {
        'financial-responsible': 'financeiro',
        'pedagogical-responsible': 'pedagogico'
      }

      return types[this.personContext?.type] || null
    },
    responsibleLabel () {
      return this.responsibleType === 'pedagogico'
        ? 'pedagógico'
        : 'financeiro'
    },
    isResponsible () {
      return Boolean(this.responsibleType && this.personContext?.idMatricula)
    },
    responsibleStatusContent () {
      const contents = {
        synchronized: {
          tone: 'success',
          title: 'Usuário ativo e sincronizado',
          description: 'O e-mail de login é o mesmo e-mail salvo no cadastro.',
          actionLabel: ''
        },
        email_mismatch: {
          tone: 'warning',
          title: 'E-mail de login divergente',
          description: 'O usuário está ativo, mas usa outro e-mail para entrar.',
          actionLabel: 'Sincronizar e-mail de login'
        },
        inactive: {
          tone: 'warning',
          title: 'Usuário de acesso inativo',
          description: 'O cadastro existe, mas não está ativo para acesso.',
          actionLabel: 'Reativar e sincronizar'
        },
        missing: {
          tone: 'error',
          title: 'Usuário de acesso não existe',
          description: 'Ainda não há um usuário vinculado a este responsável.',
          actionLabel: 'Criar usuário de acesso'
        },
        missing_email: {
          tone: 'error',
          title: 'Responsável sem e-mail válido',
          description: 'Salve um e-mail válido antes de criar ou sincronizar o acesso.',
          actionLabel: ''
        },
        email_conflict: {
          tone: 'error',
          title: 'E-mail vinculado a outra pessoa',
          description: 'A correção automática foi bloqueada para evitar acesso à conta errada.',
          actionLabel: ''
        },
        multiple_active: {
          tone: 'error',
          title: 'Mais de um usuário ativo encontrado',
          description: 'O conflito precisa ser corrigido manualmente antes da sincronização.',
          actionLabel: ''
        },
        multiple_inactive: {
          tone: 'error',
          title: 'Mais de um usuário inativo encontrado',
          description: 'O conflito precisa ser corrigido manualmente antes da reativação.',
          actionLabel: ''
        }
      }

      return contents[this.responsibleUserStatus?.status] || {
        tone: 'error',
        title: 'Situação de acesso não reconhecida',
        description: 'Atualize a consulta ou acione a equipe de Sistemas.',
        actionLabel: ''
      }
    }
  },
  mounted () {
    this.getPessoa()
  },
  methods: {
    getPessoa () {
      window.spinner.emit('open')
      MatriculaService.getPessoaByIdPessoaOuCpf(this.idPessoa, null)
        .then(response => {
          const dados = response.data.dados
          console.log("getPessoa: melo", dados)
          // Converte a data de nascimento DD/MM/YYYY → YYYY-MM-DD para o input type="date"
          if (dados?.Basicos?.dataNascimento) {
            dados.Basicos.dataNascimento = unformatDate(dados.Basicos.dataNascimento)
          }
          // Sanitiza as chaves para o padrão esperado por Pessoas.vue: Basicos, documento, endereco
          this.loadPessoa = dados ? {
            Basicos:   dados.Basicos   ?? null,
            documento: dados.Documento ?? null,
            endereco:  dados.Endereco  ?? null,
            aluno: {
              idAluno: dados.Aluno?.idAluno ?? null,
              ra: dados.Aluno?.ra ?? null,
              rm: dados.Aluno?.rm ?? null,
              idUnidade: dados.Aluno?.idUnidade ?? null,
              emailAcademico: dados.Aluno?.emailAcademico ?? null,
              escolaAnterior: dados.Aluno?.escolaAnterior ?? null,
            }
          } : null
          // Só renderiza o form depois dos dados prontos, garantindo o auto-preenchimento
          this.loaded = true

          if (this.isResponsible) {
            this.loadResponsibleUserStatus()
          }
        })
        .catch(error => {
          console.error('getPessoa erro:', error)
          window.toaster.emit('open', { style: 'error', message: 'Não foi possível carregar os dados da pessoa.', floater: true })
        })
        .finally(() => {
          window.spinner.emit('close')
        })
    },
    loadResponsibleUserStatus () {
      if (!this.isResponsible) return

      this.responsibleUserLoading = true
      this.responsibleUserError = null

      MatriculaService.getResponsavelUserStatus(
        this.responsibleType,
        this.personContext.idMatricula,
        this.idPessoa
      )
        .then(response => {
          this.responsibleUserStatus = response.data.data
        })
        .catch(error => {
          this.responsibleUserStatus = null
          this.responsibleUserError = error?.response?.data?.message
            || 'Não foi possível consultar o usuário do responsável.'
        })
        .finally(() => {
          this.responsibleUserLoading = false
        })
    },
    syncResponsibleUser () {
      if (
        !this.isResponsible
        || !this.responsibleUserStatus?.acao
        || this.responsibleUserActionLoading
      ) return

      this.responsibleUserActionLoading = true
      this.responsibleUserError = null

      MatriculaService.syncResponsavelUser(
        this.responsibleType,
        this.personContext.idMatricula,
        this.idPessoa
      )
        .then(response => {
          this.responsibleUserStatus = response.data.data
          window.toaster.emit('open', {
            style: 'success',
            message: response.data.message,
            floater: true
          })
        })
        .catch(error => {
          const message = error?.response?.data?.message
            || 'Não foi possível atualizar o usuário do responsável.'

          window.toaster.emit('open', {
            style: 'error',
            message,
            floater: true
          })

          this.loadResponsibleUserStatus()
        })
        .finally(() => {
          this.responsibleUserActionLoading = false
        })
    },
    savePessoa () {
      // Lê o estado atual do form direto do store (Pessoas/Endereco mutam matricula[branch])
      this.$store.dispatch('matricula/get').then(matricula => {
        const branch = matricula[this.branch]
        console.log("SAVE: ", branch)
        // O GET sobrescreve o branch inteiro com o retorno da API (idPessoa, dataCadastro, tipo, etc).
        // Aqui montamos o payload apenas com os campos definidos na model Basic.
        const data = {
          Basicos: {
            idPessoa: branch.Basicos.idPessoa,
            idNacionalidade: branch.Basicos.idNacionalidade,
            nome: branch.Basicos.nome,
            dataNascimento: branch.Basicos.dataNascimento,
            sexo: branch.Basicos.sexo,
            idRacaCor: branch.Basicos.idRacaCor,
            email: branch.Basicos.email,
            telefonecelular: branch.Basicos.telefonecelular,
            telefonefixo: branch.Basicos.telefonefixo,
            rendaFamiliar: branch.Basicos.rendaFamiliar,
            quantidadeResidentes: branch.Basicos.quantidadeResidentes,
            rescodigo: branch.Basicos.rescodigo
          },
          endereco: {
            idEndereco: branch.endereco.idEndereco,
            idCidade: branch.endereco.idCidade,
            cep: branch.endereco.cep,
            logradouro: branch.endereco.logradouro,
            numero: branch.endereco.numero,
            complemento: branch.endereco.complemento,
            bairro: branch.endereco.bairro,
            idUf: branch.endereco.idUf,
          },
          documento: {
            idDocumento: branch.documento.idDocumento,
            cpf: branch.documento.cpf,
            rg: branch.documento.rg,
            rgEmissor: branch.documento.rgEmissor,
          },
        }
        if (this.branch === 'aluno' || this.branch === 'BasicAluno') {
          data.aluno = {
            idAluno: branch.aluno?.idAluno ?? matricula.matricula?.idAluno,
            ra: branch.aluno?.ra,
            rm: branch.aluno?.rm,
            idUnidade: branch.aluno?.idUnidade,
            emailAcademico: branch.aluno?.emailAcademico,
            escolaAnterior: branch.aluno?.escolaAnterior,
          }
        }

        window.spinner.emit('open')
        // Edição sempre envia o idPessoa → savePessoaNew cai no updatePessoaNewFull
        MatriculaService.savePessoaNew(data)
          .then(response => {
            window.toaster.emit('open', { style: 'success', message: response?.data?.message || 'Pessoa salva com sucesso.', floater: true })
            // Mantém o spinner por 2s e então fecha a modal
            setTimeout(() => {
              window.spinner.emit('close')
              this.$emit('closeModalPessoa', true)
            }, 2000)
          })
          .catch(error => {
            window.spinner.emit('close')
            window.toaster.emit('open', { style: 'error', message: error?.response?.data?.error || 'Não foi possível salvar a pessoa.', floater: true })
          })
      })
    },
    closeModalPessoa () {
      this.$emit('closeModalPessoa')
    }
  }
}
</script>

<style lang="scss" scoped>
.mainSection {
  position: relative;
}

.access-section {
  margin-bottom: 20px;
  padding: $hmg_mid;
  border: $border-component;
  border-radius: $border-radius-mid;
  background: $color-secondary;
}

.access-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $mg_small;
  margin-bottom: $mg_small;

  h5 {
    margin-bottom: $hmg_mini;
  }

  p {
    margin: 0;
    color: $color-text;
  }
}

.access-retry {
  padding: 0;
  border: 0;
  background: transparent;
  color: $color-primary;
  font-size: $font-size-small;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: .55;
  }
}

.access-loading,
.access-state {
  display: flex;
  align-items: flex-start;
  gap: $mg_small;
  padding: $mg_small;
  border-radius: $border-radius-mid;
}

.access-loading {
  align-items: center;
  color: $color-text;
  background: $color-bg;
}

.access-loader {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  border: 2px solid $color-component;
  border-top-color: $color-primary;
  border-radius: 50%;
  animation: access-spin .75s linear infinite;
}

.access-state {
  border: 1px solid transparent;

  &--success {
    border-color: rgba(31, 225, 136, .38);
    background: rgba(31, 225, 136, .08);

    .access-indicator {
      background: $color-success-2;
    }
  }

  &--warning {
    border-color: rgba(255, 193, 7, .5);
    background: rgba(255, 193, 7, .09);

    .access-indicator {
      background: $color-warning;
    }
  }

  &--error {
    border-color: rgba(237, 28, 36, .28);
    background: rgba(237, 28, 36, .055);

    .access-indicator {
      background: $color-primary;
    }
  }

  p {
    margin: $hmg_mini 0 0;
    color: $color-text;
  }
}

.access-indicator {
  width: 10px;
  height: 10px;
  flex: 0 0 10px;
  margin-top: 4px;
  border-radius: 50%;
}

.access-content {
  min-width: 0;
  flex: 1;

  small {
    display: block;
    margin-top: $mg_small;
    color: $color-text;
  }
}

.access-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $mg_small;

  .btn {
    flex: 0 0 auto;
  }
}

.access-emails {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $mg_small;
  margin: $mg_small 0 0;

  div {
    min-width: 0;
    padding-top: $mg_mini;
    border-top: $border-component;
  }

  dt {
    margin-bottom: $hmg_mini;
    color: $color-text;
    font-size: $font-size-mini;
    font-weight: 700;
    text-transform: uppercase;
  }

  dd {
    overflow-wrap: anywhere;
    margin: 0;
    color: $color-dev;
  }
}

@keyframes access-spin {
  to {
    transform: rotate(360deg);
  }
}

@media screen and (max-width: 720px) {
  .access-summary {
    align-items: stretch;
    flex-direction: column;

    .btn {
      width: 100%;
    }
  }

  .access-emails {
    grid-template-columns: 1fr;
  }
}

form {
  padding: 0 $hmg_mid $mg_large;
}
</style>
